import React, { useState, useEffect } from 'react';
import { Spinner, Alert } from 'react-bootstrap';
import supabase from '../supabase';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ActivityCalendar({ year, month, activities, selectedDay, onDayClick }) {
  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const isToday = (d) => d && today.getFullYear() === year && today.getMonth() + 1 === month && today.getDate() === d;

  const byDay = {};
  for (const a of activities) {
    const day = parseInt(a.date.slice(8, 10), 10);
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(a);
  }

  const cells = [...new Array(firstDow).fill(null)];
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DOW.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '0.7rem', color: 'var(--text-muted)', padding: '4px 0', fontWeight: 600 }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((d, i) => {
          const acts = d ? (byDay[d] || []) : [];
          const sel = d === selectedDay;
          const tod = isToday(d);
          return (
            <div key={i} onClick={() => d && onDayClick(d)}
              style={{
                minHeight: 58,
                borderRadius: 6,
                background: sel ? 'var(--accent, #7c8cf8)' : acts.length > 0 ? 'rgba(124,140,248,0.1)' : 'transparent',
                border: tod ? '1px solid var(--accent, #7c8cf8)' : '1px solid transparent',
                cursor: d ? 'pointer' : 'default',
                padding: '4px 6px',
                transition: 'background 0.15s',
              }}>
              {d && (
                <>
                  <div style={{ fontSize: '0.78rem', fontWeight: tod ? 700 : 400, color: sel ? '#fff' : tod ? 'var(--accent, #7c8cf8)' : 'var(--text)' }}>{d}</div>
                  {acts.slice(0, 3).map((a, j) => (
                    <div key={j} style={{ fontSize: '0.63rem', color: sel ? 'rgba(255,255,255,0.85)' : 'var(--accent, #7c8cf8)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>
                      {a.name || a.category}
                    </div>
                  ))}
                  {acts.length > 3 && (
                    <div style={{ fontSize: '0.6rem', color: sel ? 'rgba(255,255,255,0.6)' : 'var(--text-muted)' }}>+{acts.length - 3} more</div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Activities() {
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    async function fetchActivities() {
      setLoading(true);
      const pad = (n) => String(n).padStart(2, '0');
      const firstDate = `${calYear}-${pad(calMonth)}-01`;
      const lastDay = new Date(calYear, calMonth, 0).getDate();
      const lastDate = `${calYear}-${pad(calMonth)}-${pad(lastDay)}`;

      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .gte('date', firstDate)
        .lte('date', lastDate)
        .order('date', { ascending: false });

      if (error) { setError(error.message); setLoading(false); return; }
      setActivities(data || []);
      setSelectedDay(null);
      setLoading(false);
    }
    fetchActivities();
  }, [calYear, calMonth]);

  function prevMonth() {
    if (calMonth === 1) { setCalYear(y => y - 1); setCalMonth(12); }
    else setCalMonth(m => m - 1);
  }
  function nextMonth() {
    if (calMonth === 12) { setCalYear(y => y + 1); setCalMonth(1); }
    else setCalMonth(m => m + 1);
  }

  const monthLabel = new Date(calYear, calMonth - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  const [monthName] = monthLabel.split(' ');

  const displayed = selectedDay
    ? activities.filter(a => parseInt(a.date.slice(8, 10), 10) === selectedDay)
    : activities;

  const cols = activities.length > 0
    ? Object.keys(activities[0]).filter(k => k !== 'id' && k !== 'user_id')
    : [];

  return (
    <div>
      <div className="page-header">
        <h1>Activities</h1>
        <p>{activities.length} activities in {monthLabel}</p>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* Calendar */}
      <div className="d-card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <button onClick={prevMonth} style={navBtnStyle}>‹</button>
          <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: '0.95rem' }}>{monthLabel}</span>
          <button onClick={nextMonth} style={navBtnStyle}>›</button>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}><Spinner animation="border" size="sm" /></div>
        ) : (
          <ActivityCalendar
            year={calYear}
            month={calMonth}
            activities={activities}
            selectedDay={selectedDay}
            onDayClick={(d) => setSelectedDay(prev => prev === d ? null : d)}
          />
        )}
        {selectedDay && (
          <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            Showing {monthName} {selectedDay} —{' '}
            <button onClick={() => setSelectedDay(null)} style={{ background: 'none', border: 'none', color: 'var(--accent, #7c8cf8)', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}>
              show all
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      {!loading && (displayed.length === 0 ? (
        <div className="d-card">
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>
            No activities {selectedDay ? `on ${monthName} ${selectedDay}` : 'this month'}.
          </p>
        </div>
      ) : (
        <div className="d-card" style={{ overflowX: 'auto', padding: 0 }}>
          <table className="d-table">
            <thead>
              <tr>{cols.map(c => <th key={c}>{c.replace(/_/g, ' ')}</th>)}</tr>
            </thead>
            <tbody>
              {displayed.map(row => (
                <tr key={row.id}>
                  {cols.map(c => <td key={c}>{formatCell(row[c])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function formatCell(val) {
  if (val === null || val === undefined) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  if (typeof val === 'boolean') return val ? <span className="pill pill-success">yes</span> : <span className="pill pill-muted">no</span>;
  if (Array.isArray(val)) return val.join(', ') || '—';
  if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}T/)) return new Date(val).toLocaleString();
  if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}$/)) return new Date(val + 'T00:00:00').toLocaleDateString();
  return String(val);
}

const navBtnStyle = {
  background: 'none',
  border: '1px solid var(--border, #333)',
  borderRadius: 4,
  color: 'var(--text)',
  cursor: 'pointer',
  padding: '2px 12px',
  fontSize: '1.2rem',
  lineHeight: 1.4,
};
